import React, { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'react-use';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';

import { Order } from 'types/ShoppingCart';
import { removeProduct, updateOrderAmount } from 'actions/ShoppingCart';
import { getAllowFractionalProductQuantities } from 'reducers/configs/settings';
import { RootState } from 'reducers';
import { useRunWithQueue } from 'utils/hooks/useRunWithQueue';

import {
  getAutoFocusInputValue,
  getDisableOrderQty,
  getOrderAmount,
  sanitizeValue,
} from '../utils';

const SimpleRowQuantity = React.memo<Pick<Order, 'orderIndex'>>(
  ({ orderIndex }) => {
    const dispatch: ThunkDispatch<RootState, unknown, Action> = useDispatch();
    const orderAmount = useSelector(getOrderAmount(orderIndex));
    const autoFocus = useSelector(getAutoFocusInputValue(orderIndex));
    const disabled = useSelector(getDisableOrderQty(orderIndex));
    const [tempAmount, setTempAmount] = useState<typeof orderAmount | null>(
      null,
    );
    const isFractionalQuantityAllowed = useSelector(
      getAllowFractionalProductQuantities,
    );
    const ref = useRef<HTMLInputElement>(null);
    const runWithQueue = useRunWithQueue();

    // Avoids dispatching parallel calculate actions while user is typing
    const [doneUpdatingOrderAmount, cancelUpdateAmountEffect] = useDebounce(
      async () => {
        if (([null, '', '-'] as Array<typeof tempAmount>).includes(tempAmount))
          return;
        // Enqueued to enforce correct order of operations if amount update does not get debounced
        runWithQueue(async () => {
          await dispatch(updateOrderAmount(tempAmount, orderIndex));
          setTempAmount(prev => (prev !== tempAmount ? prev : null));
        });
      },
      700,
      [tempAmount],
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const qty = sanitizeValue(e.target.value);
      const isAcceptableValue = qty === '-' || !Number.isNaN(Number(qty));
      const respectsFractionalQtySetting =
        isFractionalQuantityAllowed ||
        (!isFractionalQuantityAllowed && !qty.includes('.'));

      if (
        isAcceptableValue &&
        respectsFractionalQtySetting &&
        qty !== tempAmount
      ) {
        setTempAmount(qty);
      }
    };

    const handleBlur = async () => {
      if (tempAmount === '-') {
        runWithQueue(async () => dispatch(updateOrderAmount(-1, orderIndex)));
        return;
      }
      // User cleared the field - reset to prev state (if it's not 0)
      if (tempAmount === '') {
        if (Math.abs(Number(orderAmount)) === 0) {
          dispatch(removeProduct(orderIndex));
        } else {
          setTempAmount(orderAmount);
        }
        return;
      }
      // Latest amount entered by user was set as order amount already
      if (tempAmount === null) {
        // Remove the row if it's amount is 0
        if (Math.abs(Number(orderAmount)) === 0) {
          dispatch(removeProduct(orderIndex));
          // fall through to the return
        }
        // Do nothing otherwise and just return
        return;
      }
      // Latest amount entered by user still not set as order amount
      // Order amount is in the process of being updated (!done() would also check if the action has been cancelled) - cancel the updateOrderAmount execution and either remove the row if user set the qty as 0 or restart the updateOrderAmount with latest user entered value
      if (doneUpdatingOrderAmount() === false) {
        /* 
        isUpdatingOrderAmount return value becomes true only after the debounced effect is called.
        Thus there is a small time window when the debounced effect is already scheduled to run
        after a time out but isUpdatingOrderAmount returns false. In such case updateOrderAmount
        will be called twice. To avoid this - cancel the effect when handleBlur calls updateOrderAmount
        */
        cancelUpdateAmountEffect();
        if (Math.abs(Number(tempAmount)) === 0) {
          dispatch(removeProduct(orderIndex));
          setTempAmount(null);
        } else {
          runWithQueue(async () => {
            await dispatch(updateOrderAmount(tempAmount, orderIndex));
            setTempAmount(null);
          });
        }
      } else if (doneUpdatingOrderAmount() === true) {
        if (
          // The updateOrderAmount finished, but the tempAmount was not yet reset
          tempAmount &&
          Math.abs(Number(tempAmount)) === 0 &&
          Number(orderAmount) === 0
        ) {
          dispatch(removeProduct(orderIndex));
          setTempAmount(null);
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        ref.current?.blur();
      }
    };

    return (
      <input
        type="text"
        ref={ref}
        id="autoFocusInput"
        value={tempAmount !== null ? tempAmount : orderAmount}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onClick={e => e.stopPropagation()}
        disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        autoComplete="off"
        onFocus={autoFocus ? e => e.target.select() : undefined}
      />
    );
  },
);

export default SimpleRowQuantity;
